import React from "react";
import { Share2 } from "lucide-react";
import { waLink, WHATSAPP_DISPLAY, EMAIL, DEFAULT_WA_MESSAGE } from "../lib/whatsapp";

const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=0a0a0a&bgcolor=f9f8f6&data=${encodeURIComponent(waLink(DEFAULT_WA_MESSAGE))}`;

const services = [
  "Serveurs & serveuses en tenue d'apparat",
  "Maître d'hôtel — coordination complète",
  "Hôtes & hôtesses d'accueil souriants",
  "Mariages · Vins d'honneur · Conférences · Réceptions privées",
];

const FlyerCard = () => (
  <div
    id="flyer-print"
    data-testid="flyer-card"
    className="grain relative mx-auto aspect-[148/210] w-full max-w-[480px] overflow-hidden rounded-2xl border border-gold/30 bg-[#0a0a0a] shadow-2xl"
  >
    <div className="pointer-events-none absolute inset-4 rounded-xl border border-gold/15" />
    <div className="relative flex h-full flex-col px-10 py-12">
      <div className="text-center">
        <p className="font-display text-5xl tracking-tight text-white">
          FK<span className="text-gold">.</span>
        </p>
        <p className="mt-2 text-[9px] font-semibold uppercase tracking-[0.5em] text-gold">Conseil &amp; Événement</p>
        <div className="mx-auto my-5 h-px w-16 bg-gold" />
        <p className="font-display text-2xl italic tracking-tight text-white">
          L'art du service <span className="text-gold">d'exception.</span>
        </p>
      </div>
      <ul className="mt-9 space-y-3.5">
        {services.map((s) => (
          <li key={s} className="flex items-start gap-3 text-[13px] leading-snug text-white/75">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
            {s}
          </li>
        ))}
      </ul>
      <div className="mt-8 rounded-xl border border-white/10 bg-white/5 px-6 py-5 text-center">
        <p className="text-[9px] font-semibold uppercase tracking-[0.35em] text-gold">Nos tarifs</p>
        <p className="mt-2.5 font-display text-lg text-white">
          Serveurs dès <span className="text-gold">50 €/h</span> · Maître d'hôtel <span className="text-gold">70 €/h</span>
        </p>
      </div>
      <p className="mt-6 text-center text-[11px] leading-relaxed text-white/45">
        Basés en Île-de-France — déplacements dans toute la France &amp; l'Europe
        <br />
        <span className="text-white/30">(frais de déplacement à la charge du client)</span>
      </p>
      <div className="mt-auto flex items-end justify-between gap-4 pt-8">
        <div className="text-left">
          <p className="text-[9px] font-semibold uppercase tracking-[0.35em] text-gold">Réservez sur WhatsApp</p>
          <p className="mt-1.5 font-display text-2xl text-white">{WHATSAPP_DISPLAY}</p>
          <p className="mt-1 text-[10px] text-white/45">{EMAIL}</p>
          <p className="mt-0.5 text-[10px] font-semibold text-gold">www.fk-evenement.fr</p>
        </div>
        <img
          src={QR_URL}
          alt="QR code WhatsApp FK Conseil & Événement"
          data-testid="flyer-qr"
          className="h-20 w-20 rounded-lg border border-gold/25 bg-[#f9f8f6] p-1"
        />
      </div>
    </div>
  </div>
);

export default function Flyer() {
  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "https://fk-evenement.fr/flyer";
    const shareData = {
      title: "FK Conseil & Événement — Flyer A5",
      text: "Flyer A5 FK Conseil & Événement",
      url,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (e) {
      // fallback
    }
    const wa = `https://wa.me/?text=${encodeURIComponent(`${shareData.text} — ${url}`)}`;
    if (typeof window !== "undefined") window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#141414] px-6 py-20" data-testid="flyer-page">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #flyer-print, #flyer-print * { visibility: visible; }
          #flyer-print { position: absolute; inset: 0; width: 148mm; max-width: 148mm; margin: 0 auto; box-shadow: none; border-radius: 0; }
        }
      `}</style>
      <div className="mx-auto max-w-3xl">
        <a href="/" data-testid="flyer-back-link" className="text-xs uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
          ← Retour au site
        </a>
        <h1 className="mt-8 font-display text-4xl tracking-tight text-white sm:text-5xl">
          Flyer A5 <em className="italic text-gold">professionnel</em>
        </h1>
        <p className="mt-4 text-sm text-white/50">
          Format 148 × 210 mm — assorti à la carte de visite, prêt pour l'impression et la distribution en salles des fêtes.
        </p>
        <div className="mt-14">
          <FlyerCard />
        </div>
        <div className="mt-14 flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="flyer-print-button"
            className="rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
          >
            Imprimer le flyer
          </button>
          <button
            type="button"
            onClick={handleShare}
            data-testid="flyer-share-button"
            className="flex items-center gap-2 rounded-full border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
          >
            <Share2 className="h-4 w-4" />
            Partager le flyer
          </button>
          <a
            href="/carte-de-visite"
            data-testid="flyer-card-link"
            className="rounded-full border border-white/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Voir la carte de visite
          </a>
        </div>
      </div>
    </div>
  );
}
